import { Text } from '@chakra-ui/react';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchiveStatus } from './ArchiveStatus';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';

export interface Props extends SharedDisclosureProps {
  groupId: number;
  onArchived?: () => void;
}

export function ArchiveCompositionGroupModal({
  groupId,
  isOpen,
  onClose,
  onArchived,
}: Props) {
  const { t } = useTranslation();
  const { data, refetch, loading, error } = useApiResource(
    compositionGroupApi.archiveStatus,
  );

  useEffect(() => {
    refetch(groupId);
  }, [groupId, refetch]);

  return (
    <VerifyDialogWithRequest
      headerTitle={t('general.archive')}
      secondaryButtonTitle={t('general.cancel')}
      primaryButtonTitle={t('general.confirm')}
      primaryRequest={compositionGroupApi.archive}
      onPerformed={() => {
        compositionGroupEvents.onArchived.dispatch();
        if (onArchived) onArchived();
      }}
      args={[groupId]}
      isOpen={isOpen}
      onClose={onClose}
      onSuccessTitle={t('general.successfullyArchived')}
      primaryButtonDisabled={loading || (!!data && data.acceptedPas.length > 0)}
    >
      {loading && (
        <SpinnerWithLabel label={t('archive.status.loadingStatus')} />
      )}
      {error && <ErrorDetails error={error} />}
      {data && (
        <ArchiveStatus
          pendingPasToCancel={data.pendingPasToCancel}
          paActiveSamples={data.paActiveSamples}
          acceptedPas={data.acceptedPas}
        />
      )}
      {data && data.acceptedPas.length === 0 && (
        <Text mt={2}>{t('alert.areYouSure')}</Text>
      )}
    </VerifyDialogWithRequest>
  );
}
